import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { BreadcrumbService } from '../../common/services/breadcrumb.service';
import { BreadcrumbWithLabel } from '../../common/models/breadcrumb';
import { Icon } from '../../common/components/general/icon/icon';

@Component({
  selector: 'srp-breadcrumb',
  imports: [CommonModule, RouterModule, Icon],
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class Breadcrumb {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly router = inject(Router);
  
  breadcrumbs$ = this.breadcrumbService.breadcrumbsWithLabels$;

  /**
   * Check if breadcrumb is the last item (current page)
   */
  isLast(index: number, breadcrumbs: BreadcrumbWithLabel[]): boolean {
    return index === breadcrumbs.length - 1;
  }

  /**
   * Parse URL to extract base path and query parameters
   * Handles URLs that have query params embedded in the URL string
   */
  getRouterLink(breadcrumb: BreadcrumbWithLabel): string {
    const url = breadcrumb.url || '';
    const queryIndex = url.indexOf('?');
    
    if (queryIndex !== -1) {
      return url.substring(0, queryIndex);
    }
    
    return url;
  }

  /**
   * Extract query parameters from URL or return existing queryParams
   */
  getQueryParams(breadcrumb: BreadcrumbWithLabel): { [key: string]: any } | undefined {
    // If queryParams already exists, use it
    if (breadcrumb.queryParams) {
      return breadcrumb.queryParams;
    }

    const url = breadcrumb.url || '';
    const queryIndex = url.indexOf('?');
    
    if (queryIndex !== -1) {
      const queryString = url.substring(queryIndex + 1);
      const params: { [key: string]: any } = {};
      
      queryString.split('&').forEach(param => {
        const [key, value] = param.split('=');
        if (key) {
          params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
        }
      });
      
      return Object.keys(params).length > 0 ? params : undefined;
    }
    
    return undefined;
  }

  /**
   * Navigate to breadcrumb URL
   */
  navigate(breadcrumb: BreadcrumbWithLabel, event: Event): void {
    // Let RouterLink handle navigation, but prevent if it's the last item
    if (this.isLastBreadcrumb(breadcrumb)) {
      event.preventDefault();
    }
  }

  /**
   * Navigate to breadcrumb with state containing breadcrumb trail
   */
  navigateWithBreadcrumb(breadcrumb: BreadcrumbWithLabel, index: number, breadcrumbs: BreadcrumbWithLabel[], event: Event): void {
    event.preventDefault();
    
    // Don't navigate if it's the last breadcrumb
    if (this.isLast(index, breadcrumbs)) {
      return;
    }

    // Get the breadcrumb trail up to (and including) the clicked breadcrumb
    const breadcrumbTrail = breadcrumbs.slice(0, index + 1);
    
    // Navigate with the breadcrumb trail in state
    this.router.navigate(
      [this.getRouterLink(breadcrumb)],
      { 
        queryParams: this.getQueryParams(breadcrumb),
        state: { 
          breadcrumbTrail: breadcrumbTrail.map(b => ({
            labelId: (b as any).labelId,
            label: b.label,
            url: b.url,
            page: b.page || this.breadcrumbService.getPageFromUrl(b.url),
            queryParams: this.getQueryParams(b)
          }))
        }
      }
    );
  }

  private isLastBreadcrumb(breadcrumb: BreadcrumbWithLabel): boolean {
    const breadcrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
    return breadcrumbs[breadcrumbs.length - 1] === breadcrumb;
  }
}
